import { Controller } from "@hotwired/stimulus"

// Client-side column sorting for a plain <table>. No server round-trip.
//
// Usage: put data-controller="table-sort" on the <table>. Mark each sortable
// header cell with a column index, a type, and an indicator:
//
//   <th data-table-sort-target="header" data-column="0" data-type="text" aria-sort="none">
//     <button data-action="table-sort#sort">Player
//       <span data-table-sort-target="indicator"><i class="ph ph-caret-up-down"></i></span>
//     </button>
//   </th>
//
// Rows are compared by each cell's data-sort-value, falling back to its text.
// "number" columns compare numerically; everything else compares as lowercased
// strings. Clicking a header toggles ascending/descending, and the Phosphor
// caret icon in the active header flips to match.
export default class extends Controller {
  static targets = ["header", "indicator"]

  sort(event) {
    const th = event.currentTarget.closest("th")
    const column = Number(th.dataset.column)
    const type = th.dataset.type || "text"
    const ascending = th.getAttribute("aria-sort") !== "ascending"

    const tbody = this.element.tBodies[0]
    if (!tbody) return

    const rows = Array.from(tbody.rows)
    rows.sort((a, b) => {
      const av = this.cellValue(a, column, type)
      const bv = this.cellValue(b, column, type)
      if (av < bv) return ascending ? -1 : 1
      if (av > bv) return ascending ? 1 : -1
      return 0
    })
    rows.forEach((row) => tbody.appendChild(row))

    this.reflect(th, ascending)
  }

  cellValue(row, column, type) {
    const cell = row.cells[column]
    const raw = cell?.dataset.sortValue ?? cell?.textContent.trim() ?? ""
    return type === "number" ? Number(raw) || 0 : raw.toLowerCase()
  }

  reflect(activeHeader, ascending) {
    this.headerTargets.forEach((th) => {
      const isActive = th === activeHeader
      th.setAttribute(
        "aria-sort",
        isActive ? (ascending ? "ascending" : "descending") : "none"
      )
      const icon = th.querySelector("[data-table-sort-target='indicator'] i")
      if (icon) {
        icon.classList.remove(
          "ph-caret-up",
          "ph-caret-down",
          "ph-caret-up-down"
        )
        icon.classList.add(
          isActive
            ? ascending
              ? "ph-caret-up"
              : "ph-caret-down"
            : "ph-caret-up-down"
        )
      }
    })
  }
}
